import { ClipboardCopyButton } from '@neo/blocks/clipboard-copy-button'
import { cn } from '@neo/lib/utils'
import { Badge } from '@neo/primitives/badge'
import { Button } from '@neo/primitives/button'
import { ScrollArea } from '@neo/primitives/scroll-area'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@neo/primitives/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@neo/primitives/tabs'
import { Typography } from '@neo/primitives/typography'
import InlineEdit from '@neo/shared/components/inline-edit'
import { useQueryClient } from '@tanstack/react-query'
import {
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronsLeft,
  ChevronsRight,
  ChevronUp,
  Clock,
  Code2,
  Download,
  Loader2,
  X,
  XCircle,
} from 'lucide-react'
import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { useLocation, useParams } from 'router-v6'
import { QueryError } from 'v2/api-console/shared/components/query-error'
import { formatDateTime } from 'v2/api-console/shared/utils/date-utils'
import { CodeEditor } from 'v2/shared/components/code-editor'
import { ErrorBoundary } from 'v2/shared/components/error-boundary'
import { useFeatureFlags } from 'v2/shared/hooks/use-feature-flags'
import { flowService } from 'v2/shared/services/flow-service'
import { flowQueryKeys } from 'v2/shared/utils/query-key-management'
import { errorToast } from 'v2/utils/toasts'
import {
  ConversationFlow,
  CumulativeEvalResults,
  EvalResults,
  GoalEvalResults,
  getGoalAchieved,
  getNodeScorePercent,
  type ScenarioMockInputsMap,
  ScenarioMockInputsSummary,
  scoreToTier,
  TIER_CONFIG,
  type TranscriptTurn,
} from '../components/eval/conversation-flow'
import { type SimulationTurn, useSimulationStore } from '../components/eval/simulation-store'
import { ScenarioDetailsBody } from './scenario-details-sidebar'
import { filterUserTags } from './scenario-tags'
import { SimulationRunsLoading } from './simulation-loading'
import {
  type PersistedScenario,
  prefetchSimulationRunScenario,
  useRenameSimulationRunMutation,
  useSimulationRunQuery,
  useSimulationRunScenarioQuery,
  useSimulationRunsQuery,
} from './simulation-tab.queries'
import { SimulationToolbar } from './simulation-toolbar'
import { useTagFilter } from './use-tag-filter'
import { useAppNavigate } from 'v2/shared/hooks/use-app-navigate'

const scenarioItemTags = (s: ScenarioItem) => filterUserTags(s.tags)

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value)

const CALLER_NAME_KEYS = new Set(['callername', 'customername', 'leadname', 'fullname', 'name'])

const normalizeInputKey = (key: string) => key.toLowerCase().replace(/[^a-z0-9]/g, '')

const extractScenarioRecord = (scenarioData?: unknown): Record<string, unknown> | undefined => {
  const data = scenarioData as Record<string, unknown> | undefined
  const scenario = data?.scenario
  if (isRecord(scenario)) return scenario
  return isRecord(data) ? data : undefined
}

const extractScenarioMockInputs = (scenarioData?: unknown): ScenarioMockInputsMap | undefined => {
  const scenario = extractScenarioRecord(scenarioData)
  const rawStartNodeParams = scenario?.start_node_params ?? scenario?.startNodeParams
  const startNodeParams = isRecord(rawStartNodeParams) ? rawStartNodeParams : {}
  const inputs = Object.fromEntries(
    Object.entries(startNodeParams).filter(([, value]) => value != null),
  )

  const hasNameInput = Object.keys(inputs).some((key) => normalizeInputKey(key).includes('name'))
  const persona = isRecord(scenario?.persona) ? scenario.persona : undefined
  const details = isRecord(persona?.details) ? persona.details : undefined
  if (!hasNameInput && details) {
    const callerName = Object.entries(details).find(
      ([key, value]) => CALLER_NAME_KEYS.has(normalizeInputKey(key)) && value != null,
    )
    if (callerName) inputs[callerName[0]] = callerName[1]
  }

  return Object.keys(inputs).length ? inputs : undefined
}

interface SimulationsPanelProps {
  onBack?: () => void
}

export const SimulationsPanel = ({ onBack }: SimulationsPanelProps) => {
  const { id: flowId = '' } = useParams()
  const location = useLocation()
  const navigate = useAppNavigate()
  const phloUuid = flowId

  // --- URL-derived state ---
  const basePath = `/agent/builder/${flowId}/simulation/results`
  const pathAfterBase = location.pathname.startsWith(basePath)
    ? location.pathname.slice(basePath.length)
    : ''
  const segments = pathAfterBase.split('/').filter(Boolean)
  const selectedRunUuid = segments[0] || null
  const selectedScenarioUuid = segments[1] || null

  const queryClient = useQueryClient()
  const {
    data: runsData,
    isLoading: runsLoading,
    isError: runsError,
    refetch: refetchRuns,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useSimulationRunsQuery(phloUuid)
  const [isRetryingRuns, setIsRetryingRuns] = useState(false)
  const runs =
    runsData?.pages
      .flatMap((p) => p.runs)
      .filter((r) => r.status === 'completed' || r.status === 'running') ?? []
  const renameMutation = useRenameSimulationRunMutation(phloUuid)
  const [exportingRunUuid, setExportingRunUuid] = useState<string | null>(null)
  const [expandedScenarioUuid, setExpandedScenarioUuid] = useState<string | null>(null)
  const { isDebuggingMode } = useFeatureFlags()
  const [isRailCollapsed, setIsRailCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true
    return localStorage.getItem('simulation-runs-rail-collapsed') !== 'false'
  })
  const toggleRail = useCallback(() => {
    setIsRailCollapsed((prev) => {
      const next = !prev
      localStorage.setItem('simulation-runs-rail-collapsed', String(next))
      return next
    })
  }, [])
  const handleExportRun = useCallback(async (runUuid: string, label: string) => {
    setExportingRunUuid(runUuid)
    try {
      const resp = (await flowService.exportSimulationRun(runUuid)) as {
        data: unknown
      }
      const blob = new Blob([JSON.stringify(resp.data, null, 2)], {
        type: 'application/json',
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const safeLabel = label.replace(/[^a-z0-9-_]+/gi, '-').toLowerCase()
      a.download = `${safeLabel || 'simulation-run'}-${runUuid}.json`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch {
      errorToast({ title: 'Failed to export simulation run' })
    } finally {
      setExportingRunUuid(null)
    }
  }, [])
  const { ref: runsScrollSentinelRef, inView: runsInView } = useInView()

  useEffect(() => {
    if (runsInView && hasNextPage) fetchNextPage()
  }, [runsInView, hasNextPage, fetchNextPage])

  const simulationStatus = useSimulationStore((s) => s.simulationStatus)
  const activeRunUuid = useSimulationStore((s) => s.activeRunUuid)
  const scenarioOrder = useSimulationStore((s) => s.scenarioOrder)
  const scenarioStates = useSimulationStore((s) => s.scenarioStates)

  // Invalidate runs list when a live run completes
  const prevStatus = useRef(simulationStatus)
  useEffect(() => {
    if (prevStatus.current !== 'completed' && simulationStatus === 'completed') {
      queryClient.invalidateQueries({
        queryKey: flowQueryKeys().simulationRuns(phloUuid),
      })
    }
    prevStatus.current = simulationStatus
  }, [simulationStatus, phloUuid, queryClient])

  const isActiveStatus =
    simulationStatus === 'enqueued' ||
    simulationStatus === 'started' ||
    simulationStatus === 'running'
  const hasLiveRun = isActiveStatus && !!activeRunUuid

  // Auto-select: prefer live run, otherwise first historical run
  useEffect(() => {
    if (!selectedRunUuid) {
      if (hasLiveRun && activeRunUuid) {
        navigate(`${basePath}/${activeRunUuid}`, { replace: true })
      } else if (runs.length > 0) {
        navigate(`${basePath}/${runs[0].uuid}`, { replace: true })
      }
    }
  }, [selectedRunUuid, hasLiveRun, activeRunUuid, runs, basePath, navigate])

  // Collapse the debug JSON row when the active run changes: scenario UUIDs can
  // repeat across runs, so a stale expanded UUID would auto-expand a row in the
  // new run that the user never clicked.
  useEffect(() => {
    setExpandedScenarioUuid(null)
  }, [selectedRunUuid])

  const isLiveSelected = selectedRunUuid === activeRunUuid && hasLiveRun

  const { data: runDetail } = useSimulationRunQuery(isLiveSelected ? null : selectedRunUuid)
  const { data: scenarioDetail } = useSimulationRunScenarioQuery(
    isLiveSelected ? null : selectedRunUuid,
    isLiveSelected ? null : selectedScenarioUuid,
  )

  const handleScenarioPrefetch = useCallback(
    (scenarioUuid: string) => {
      if (isLiveSelected || !selectedRunUuid) return
      prefetchSimulationRunScenario(queryClient, selectedRunUuid, scenarioUuid)
    },
    [queryClient, selectedRunUuid, isLiveSelected],
  )

  // Build scenario list from live store or historical data
  const scenarios: ScenarioItem[] = useMemo(() => {
    if (isLiveSelected) {
      return scenarioOrder.map((id) => {
        const s = scenarioStates[id]
        return {
          uuid: s?.dbUuid ?? id,
          scenarioName: s?.scenario.name ?? 'Unknown',
          status: s?.status ?? 'pending',
          tags: s?.scenario.tags ?? [],
          evaluation: s?.result?.evaluation as Record<string, unknown> | undefined,
          debugPayload: s ?? { id },
        }
      })
    }
    return (runDetail?.scenarios ?? []).map((s, idx) => ({
      uuid: s.uuid,
      scenarioName: s.scenario_name || `Scenario ${idx + 1}`,
      status: s.status,
      tags: s.tags ?? [],
      evaluation: s.evaluation,
      debugPayload: s,
    }))
  }, [isLiveSelected, scenarioOrder, scenarioStates, runDetail])

  const {
    tagOptions,
    selectedTags,
    setSelectedTags,
    filtered: filteredScenarios,
  } = useTagFilter(scenarios, scenarioItemTags)

  const liveRunGoalSummary: GoalSummary | null = useMemo(() => {
    if (!hasLiveRun) return null
    let scenariosPassed = 0
    let scenariosFailed = 0
    for (const id of scenarioOrder) {
      const s = scenarioStates[id]
      if (!s || s.status !== 'completed' || !s.result?.evaluation) continue
      const goalEval = s.result.evaluation.goal_evaluation as Record<string, unknown> | undefined
      const goals = goalEval?.goals as Array<{ achieved: boolean }> | undefined
      if (!goals?.length) continue
      if (goals.some((g) => g.achieved)) scenariosPassed++
      else scenariosFailed++
    }
    return scenariosPassed + scenariosFailed > 0 ? { scenariosPassed, scenariosFailed } : null
  }, [hasLiveRun, scenarioOrder, scenarioStates])

  const selectedScenarioData = useMemo(() => {
    if (selectedScenarioUuid === null) return null
    if (isLiveSelected) {
      const scenarioId = scenarioOrder.find(
        (id) => scenarioStates[id]?.dbUuid === selectedScenarioUuid || id === selectedScenarioUuid,
      )
      const state = scenarioId ? scenarioStates[scenarioId] : undefined
      if (!state) return null
      return {
        scenarioName: state.scenario.name,
        turns: state.turns as Array<SimulationTurn | TranscriptTurn>,
        scenarioMockInputs: extractScenarioMockInputs(state.scenario),
        isRunning: state.status === 'running',
        evaluation: state.result?.evaluation,
        stopReason: state.result?.stopReason,
        turnCount: state.result?.turns,
        error: state.result?.error,
        flowRunUuid: state.flowRunUuid,
        scenarioData: undefined as PersistedScenario | undefined,
      }
    }
    const scenario = runDetail?.scenarios.find((s) => s.uuid === selectedScenarioUuid)
    if (!scenario) return null
    return {
      scenarioName: scenario.scenario_name || 'Unknown',
      turns: (scenarioDetail?.transcript ?? []) as Array<SimulationTurn | TranscriptTurn>,
      scenarioMockInputs: extractScenarioMockInputs(scenarioDetail?.scenario_data),
      isRunning: scenario.status === 'running',
      evaluation: scenario.evaluation,
      stopReason: scenario.stop_reason,
      turnCount: scenario.turn_count,
      error: scenario.error,
      flowRunUuid: scenario.uuid,
      scenarioData: scenarioDetail?.scenario_data as PersistedScenario | undefined,
    }
  }, [
    selectedScenarioUuid,
    isLiveSelected,
    scenarioOrder,
    scenarioStates,
    runDetail,
    scenarioDetail,
  ])

  return (
    <div className="flex h-full">
      {/* Left section: toolbar + content */}
      <div className="flex flex-col flex-1 min-w-0 min-h-0">
        <SimulationToolbar
          activeTab="results"
          onTabChange={(tab) => tab === 'scenarios' && onBack?.()}
          tagOptions={tagOptions}
          selectedTags={selectedTags}
          onTagsChange={setSelectedTags}
        >
          {(() => {
            if (!selectedRunUuid || selectedRunUuid === activeRunUuid) return null
            const idx = runs.findIndex((r) => r.uuid === selectedRunUuid)
            if (idx === -1) return null
            const run = runs[idx]
            const label = run.name || `Simulation Run #${runs.length - idx}`
            const isExporting = exportingRunUuid === run.uuid
            return (
              <Button
                variant="outline"
                size="sm"
                disabled={isExporting}
                disabledReason={isExporting ? 'Export in progress' : undefined}
                data-testid="button-download-simulation-json"
                onClick={() => handleExportRun(run.uuid, label)}
              >
                {isExporting ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
                ) : (
                  <Download className="h-3.5 w-3.5 mr-1.5" />
                )}
                Download JSON
              </Button>
            )
          })()}
        </SimulationToolbar>

        {/* Content: sidebar + table */}
        {(runsError || isRetryingRuns) && (
          <div className="px-4 pt-4">
            <QueryError
              title="Couldn't load simulation runs."
              testId="button-retry-simulation-runs"
              isRetrying={isRetryingRuns}
              onRetry={async () => {
                setIsRetryingRuns(true)
                try {
                  await refetchRuns()
                } finally {
                  setIsRetryingRuns(false)
                }
              }}
            />
          </div>
        )}
        <div className="flex flex-1 min-h-0 gap-4 px-4 py-4">
          {/* Left sidebar — run list */}
          <div
            className={cn(
              'flex flex-shrink-0 flex-col overflow-hidden rounded-lg border border-border bg-card transition-[width] duration-200 ease-linear',
              isRailCollapsed ? 'w-10' : 'w-[220px]',
            )}
          >
            <div
              className={cn(
                'flex flex-shrink-0 items-center border-b border-border',
                isRailCollapsed ? 'justify-center' : 'justify-between px-3',
              )}
            >
              {!isRailCollapsed && (
                <Typography variant="S/600" className="text-secondary">
                  Runs
                </Typography>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9"
                onClick={toggleRail}
                tooltipContent={isRailCollapsed ? 'Expand runs' : 'Collapse runs'}
                tooltipSide="right"
                data-testid="button-toggle-runs-rail"
              >
                {isRailCollapsed ? (
                  <ChevronsRight className="h-4 w-4" />
                ) : (
                  <ChevronsLeft className="h-4 w-4" />
                )}
              </Button>
            </div>
            {runsLoading ? (
              <SimulationRunsLoading collapsed={isRailCollapsed} />
            ) : (runsError || isRetryingRuns) &&
              runs.length === 0 &&
              !hasLiveRun ? null : runs.length === 0 && !hasLiveRun ? (
              <div
                className={cn(
                  'flex flex-col items-center justify-center text-secondary gap-2',
                  isRailCollapsed ? 'p-2' : 'p-6',
                )}
              >
                <Clock className={isRailCollapsed ? 'h-4 w-4' : 'h-8 w-8'} />
                {!isRailCollapsed && <Typography variant="S/400">No runs yet</Typography>}
              </div>
            ) : isRailCollapsed ? (
              <div className="flex-1 overflow-y-auto py-2 flex flex-col items-center gap-1">
                {hasLiveRun && (
                  <RunDot
                    label="Live run"
                    number="●"
                    isSelected={selectedRunUuid === activeRunUuid}
                    isLive
                    onClick={() => navigate(`${basePath}/${activeRunUuid}`, { replace: true })}
                  />
                )}
                {runs
                  .filter((run) => !hasLiveRun || run.uuid !== activeRunUuid)
                  .map((run, idx, arr) => {
                    const runLabel = run.name || `Simulation Run #${arr.length - idx}`
                    return (
                      <RunDot
                        key={run.uuid}
                        label={runLabel}
                        number={`${arr.length - idx}`}
                        isSelected={selectedRunUuid === run.uuid}
                        onClick={() => navigate(`${basePath}/${run.uuid}`, { replace: true })}
                      />
                    )
                  })}
                {isFetchingNextPage && (
                  <Loader2 className="h-3 w-3 animate-spin text-secondary mt-1" />
                )}
                {hasNextPage && <div ref={runsScrollSentinelRef} className="h-1" />}
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto">
                {hasLiveRun && (
                  <RunCard
                    label="Simulation Run"
                    scenarioCount={scenarioOrder.length}
                    finishDate={null}
                    isSelected={selectedRunUuid === activeRunUuid}
                    isLive
                    goalSummary={liveRunGoalSummary}
                    onClick={() =>
                      navigate(`${basePath}/${activeRunUuid}`, {
                        replace: true,
                      })
                    }
                  />
                )}
                {runs
                  .filter((run) => !hasLiveRun || run.uuid !== activeRunUuid)
                  .map((run, idx, arr) => {
                    const runGoalSummary =
                      run.scenarios_passed + run.scenarios_failed > 0
                        ? {
                            scenariosPassed: run.scenarios_passed,
                            scenariosFailed: run.scenarios_failed,
                          }
                        : null
                    const runLabel = run.name || `Simulation Run #${arr.length - idx}`
                    return (
                      <RunCard
                        key={run.uuid}
                        label={runLabel}
                        scenarioCount={run.scenario_count}
                        finishDate={run.completed_at}
                        isSelected={selectedRunUuid === run.uuid}
                        goalSummary={runGoalSummary}
                        onClick={() => navigate(`${basePath}/${run.uuid}`, { replace: true })}
                        onRename={(name) => renameMutation.mutate({ runUuid: run.uuid, name })}
                      />
                    )
                  })}
                {isFetchingNextPage && (
                  <div className="flex items-center justify-center py-3">
                    <Loader2 className="h-4 w-4 animate-spin text-secondary" />
                  </div>
                )}
                {hasNextPage && <div ref={runsScrollSentinelRef} className="h-1" />}
              </div>
            )}
          </div>

          {/* Right — scenario results table */}
          <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-border bg-card">
            {!selectedRunUuid ? (
              <div className="flex items-center justify-center flex-1 text-secondary">
                <Typography variant="P/400">Select a run to view results</Typography>
              </div>
            ) : (
              <ScrollArea className="flex-1">
                <Table>
                  <TableHeader className="bg-hv1/30 sticky top-0 z-10">
                    <TableRow className="border-border hover:bg-transparent">
                      <TableHead className="text-secondary h-10">Test Scenarios</TableHead>
                      <TableHead className="text-secondary h-10">Goal Status</TableHead>
                      <TableHead className="text-secondary h-10">Conversation Quality</TableHead>
                      {isDebuggingMode && (
                        <TableHead className="text-secondary h-10 w-[96px]">Debug</TableHead>
                      )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredScenarios.map((s) => {
                      const isExpanded = expandedScenarioUuid === s.uuid
                      const debugJson = isExpanded
                        ? JSON.stringify(s.debugPayload ?? s, null, 2)
                        : ''

                      return (
                        <Fragment key={s.uuid}>
                          <TableRow
                            className={`cursor-pointer border-border transition-colors hover:bg-hv1 ${
                              selectedScenarioUuid === s.uuid ? 'bg-hv1' : ''
                            }`}
                            onMouseEnter={() => handleScenarioPrefetch(s.uuid)}
                            onClick={() =>
                              navigate(`${basePath}/${selectedRunUuid}/${s.uuid}`, {
                                replace: true,
                              })
                            }
                          >
                            <TableCell>
                              <Typography variant="S/400">{s.scenarioName}</Typography>
                            </TableCell>
                            <TableCell>
                              <GoalStatusCell status={s.status} evaluation={s.evaluation} />
                            </TableCell>
                            <TableCell>
                              <ScoreCell status={s.status} evaluation={s.evaluation} />
                            </TableCell>
                            {isDebuggingMode && (
                              <TableCell className="w-[96px]">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-7 gap-1.5 px-2"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setExpandedScenarioUuid((current) =>
                                      current === s.uuid ? null : s.uuid,
                                    )
                                  }}
                                  aria-expanded={isExpanded}
                                  data-testid="button-toggle-run-scenario-json"
                                >
                                  <Code2 className="h-3.5 w-3.5" />
                                  JSON
                                  {isExpanded ? (
                                    <ChevronUp className="h-3.5 w-3.5" />
                                  ) : (
                                    <ChevronDown className="h-3.5 w-3.5" />
                                  )}
                                </Button>
                              </TableCell>
                            )}
                          </TableRow>
                          {isDebuggingMode && isExpanded && (
                            <TableRow className="border-border bg-bg2/40 hover:bg-bg2/40">
                              <TableCell colSpan={4} className="p-0">
                                <div className="min-w-0 px-6 py-3">
                                  <div className="mb-2 flex items-center justify-between gap-3">
                                    <Typography variant="S/600" className="text-secondary">
                                      Raw Run Scenario JSON
                                    </Typography>
                                    <ClipboardCopyButton
                                      textToCopy={debugJson}
                                      tooltipText="Copy JSON"
                                      className="h-8 w-8"
                                      data-testid="button-copy-run-scenario-json"
                                    />
                                  </div>
                                  <div className="min-w-0 max-w-full overflow-hidden rounded-md border border-border">
                                    <CodeEditor
                                      className="[&_.cm-editor]:bg-background [&_.cm-scroller]:max-h-[360px] [&_.cm-scroller]:overflow-y-auto"
                                      value={debugJson}
                                      readOnly
                                      lineWrapping
                                      showLineNumbers={false}
                                      onChange={() => {}}
                                    />
                                  </div>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </Fragment>
                      )
                    })}
                  </TableBody>
                </Table>
              </ScrollArea>
            )}
          </div>
        </div>
      </div>

      {selectedScenarioData && (
        <ErrorBoundary>
          <TranscriptSidebar
            {...selectedScenarioData}
            onClose={() => navigate(`${basePath}/${selectedRunUuid}`, { replace: true })}
          />
        </ErrorBoundary>
      )}
    </div>
  )
}

// --- Transcript Sidebar ---

const TranscriptSidebar = ({
  scenarioName,
  turns,
  scenarioMockInputs,
  isRunning,
  evaluation,
  stopReason,
  turnCount,
  error,
  flowRunUuid,
  scenarioData,
  onClose,
}: {
  scenarioName: string
  turns: Array<SimulationTurn | TranscriptTurn>
  scenarioMockInputs?: ScenarioMockInputsMap
  isRunning: boolean
  evaluation?: Record<string, unknown> | null
  stopReason?: string
  turnCount?: number
  error?: string
  flowRunUuid?: string
  scenarioData?: PersistedScenario
  onClose: () => void
}) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const userScrolledUp = useRef(false)
  const prevTurnCount = useRef(turns.length)

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const onScroll = () => {
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= 60
      userScrolledUp.current = !atBottom
    }
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [])

  useLayoutEffect(() => {
    if (turns.length > prevTurnCount.current && isRunning && !userScrolledUp.current) {
      scrollToBottom()
    }
    prevTurnCount.current = turns.length
  }, [turns.length, isRunning, scrollToBottom])

  useLayoutEffect(() => {
    userScrolledUp.current = false
    scrollToBottom()
  }, [flowRunUuid, scrollToBottom])

  const simulationMode = scenarioData?.scenario.eval_metadata?.simulation_mode

  return (
    <div className="flex min-h-0 w-[400px] flex-shrink-0 flex-col border-l border-border bg-card">
      <div className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
        <div className="min-w-0 flex-1 flex items-center gap-2">
          <Typography variant="P/400" className="truncate">
            {scenarioName}
          </Typography>
          {simulationMode && (
            <Badge variant="tag" className="shrink-0">
              {simulationMode === 'smoke' ? 'Smoke' : 'Stress'}
            </Badge>
          )}
        </div>
        <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>
      <Tabs defaultValue="transcript" className="flex-1 flex flex-col min-h-0">
        <TabsList className="px-4 shrink-0">
          <TabsTrigger value="transcript">Transcript</TabsTrigger>
          <TabsTrigger value="scenario" disabled={!scenarioData}>
            Scenario
          </TabsTrigger>
        </TabsList>
        <TabsContent
          value="transcript"
          forceMount
          className="flex-1 min-h-0 mt-0 outline-none data-[state=inactive]:hidden"
        >
          <ScrollArea ref={scrollRef} className="h-full">
            <div className="p-4">
              <CumulativeEvalResults evaluation={evaluation} />
              <ScenarioMockInputsSummary inputs={scenarioMockInputs} />
              <ConversationFlow turns={turns} isRunning={isRunning} evaluation={evaluation} />
              <GoalEvalResults evaluation={evaluation} />
              {!isRunning && stopReason && (
                <EvalResults stopReason={stopReason} turns={turnCount ?? 0} error={error} />
              )}
              {flowRunUuid && (
                <div className="mt-3 pt-3 border-t border-st1">
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col gap-0.5 min-w-0">
                      <span className="text-xs-400 text-secondary truncate">
                        Flow Run UUID: {flowRunUuid}
                      </span>
                    </div>
                    <ClipboardCopyButton
                      textToCopy={`Flow Run UUID: ${flowRunUuid}`}
                      tooltipText="Copy debug IDs"
                      iconSize={12}
                    />
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>
        </TabsContent>
        <TabsContent value="scenario" className="flex-1 min-h-0 mt-0 outline-none">
          {scenarioData ? (
            <ScrollArea className="h-full">
              <ScenarioDetailsBody scenario={scenarioData} />
            </ScrollArea>
          ) : (
            <div className="p-4">
              <Typography variant="S/400" className="text-secondary">
                Scenario details not available.
              </Typography>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}

// --- Helpers ---

interface ScenarioItem {
  uuid: string
  scenarioName: string
  status: string
  tags: string[]
  evaluation?: Record<string, unknown> | null
  debugPayload?: unknown
}

const NoGoalPlaceholder = () => (
  <span className="text-xs-400 text-secondary">No goals configured</span>
)

const GoalStatusCell = ({
  status,
  evaluation,
}: {
  status: string
  evaluation?: Record<string, unknown> | null
}) => {
  if (status === 'pending') {
    return <span className="text-xs-400 text-secondary">—</span>
  }
  if (status === 'running') {
    return (
      <span className="font-jetbrains text-xs-400 text-secondary inline-flex items-center gap-1">
        <Loader2 className="h-2.5 w-2.5 animate-spin" />
        Running
      </span>
    )
  }
  if (status === 'error') {
    return <Badge variant="destructive">Error</Badge>
  }
  if (status === 'completed' && evaluation) {
    const achieved = getGoalAchieved(evaluation)
    if (achieved != null) {
      return achieved ? (
        <Badge variant="success">Achieved</Badge>
      ) : (
        <Badge variant="destructive" className="whitespace-nowrap">
          Not Achieved
        </Badge>
      )
    }
  }
  return <NoGoalPlaceholder />
}

const ScoreCell = ({
  status,
  evaluation,
}: {
  status: string
  evaluation?: Record<string, unknown> | null
}) => {
  if (status === 'running') {
    return (
      <span className="font-jetbrains text-xs-400 text-secondary inline-flex items-center gap-1">
        <Loader2 className="h-2.5 w-2.5 animate-spin" />
        Running
      </span>
    )
  }
  if (status === 'error') {
    return <Badge variant="destructive">Error</Badge>
  }
  if (status === 'completed' && evaluation) {
    const pct = getNodeScorePercent(evaluation)
    if (pct != null) {
      const tier = scoreToTier(pct / 100)
      const config = TIER_CONFIG[tier]
      return (
        <span className={`font-jetbrains text-xs-500 ${config.className}`}>{config.label}</span>
      )
    }
  }
  return <span className="text-xs-400 text-secondary">—</span>
}

interface GoalSummary {
  scenariosPassed: number
  scenariosFailed: number
}

const RunDot = ({
  label,
  number,
  isSelected,
  isLive,
  onClick,
}: {
  label: string
  number: string
  isSelected: boolean
  isLive?: boolean
  onClick: () => void
}) => (
  <button
    type="button"
    onClick={onClick}
    title={label}
    className={cn(
      'h-7 w-7 rounded-md flex items-center justify-center text-xs-500 transition-colors flex-shrink-0',
      isSelected ? 'bg-primary text-primary-foreground' : 'bg-hv1/30 text-secondary hover:bg-hv1',
      isLive && !isSelected && 'animate-pulse',
    )}
    data-testid={`button-run-dot${isSelected ? '-selected' : ''}`}
  >
    {number}
  </button>
)

const RunCard = ({
  label,
  scenarioCount,
  finishDate,
  isSelected,
  isLive,
  goalSummary,
  onClick,
  onRename,
}: {
  label: string
  scenarioCount: number
  finishDate: string | null
  isSelected: boolean
  isLive?: boolean
  goalSummary?: GoalSummary | null
  onClick: () => void
  onRename?: (name: string) => void
}) => {
  const formatted = finishDate
    ? formatDateTime(finishDate, { format: 'D MMM YYYY [at] HH:mm' })
    : null
  const canRename = !!onRename && !isLive
  const [isEditing, setIsEditing] = useState(false)
  const [pendingName, setPendingName] = useState(label)
  const isChanged = pendingName.trim() !== label && pendingName.trim().length > 0

  const handleSave = useCallback(
    (value?: string) => {
      const name = (value ?? pendingName).trim()
      if (name && name !== label) {
        onRename?.(name)
      }
      setIsEditing(false)
    },
    [pendingName, label, onRename],
  )

  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full border-b border-border p-4 text-left transition-colors ${
        isSelected ? 'border-l-2 border-l-primary bg-hv1/30' : 'hover:bg-hv1/30'
      }`}
    >
      <div className="flex items-center gap-1.5 group/name">
        {isEditing ? (
          <>
            <InlineEdit
              name="run-name"
              initialHtml={label}
              focusOnMount
              onChange={(_, value) => setPendingName(value)}
              onEnter={(_, value) => handleSave(value)}
              className="text-s-400 text-primary flex-1 min-w-0 focus-visible:ring-transparent border-0 shadow-none outline-none"
            />
            <div
              className={`transition-[width,opacity] duration-150 ${
                isChanged ? 'w-6 opacity-100' : 'w-0 opacity-0'
              }`}
            >
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={(e) => {
                  e.stopPropagation()
                  handleSave()
                }}
              >
                <Check className="h-3.5 w-3.5" />
              </Button>
            </div>
          </>
        ) : (
          <>
            <Typography
              variant="S/400"
              className={`text-primary truncate ${canRename ? 'cursor-text' : ''}`}
              onClick={
                canRename
                  ? (e) => {
                      e.stopPropagation()
                      setPendingName(label)
                      setIsEditing(true)
                    }
                  : undefined
              }
            >
              {label}
            </Typography>
            {isLive && <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />}
          </>
        )}
      </div>
      <Typography variant="XS/400" className="text-secondary mt-1 font-jetbrains tabular-nums">
        {scenarioCount} Scenarios
        {isLive && !formatted && <> &bull; In progress</>}
      </Typography>
      {formatted && (
        <Typography variant="XS/400" className="text-secondary mt-0.5 font-jetbrains tabular-nums">
          Finish: {formatted}
        </Typography>
      )}
      {goalSummary && (
        <div className="flex items-center gap-3 mt-1.5">
          <div className="flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3 text-status-success" />
            <Typography
              variant="XS/400"
              className="text-status-success font-jetbrains tabular-nums"
            >
              {goalSummary.scenariosPassed} Passed
            </Typography>
          </div>
          <div className="flex items-center gap-1">
            <XCircle className="h-3 w-3 text-destructive" />
            <Typography variant="XS/400" className="text-destructive font-jetbrains tabular-nums">
              {goalSummary.scenariosFailed} Failed
            </Typography>
          </div>
        </div>
      )}
    </button>
  )
}
